// Cache identities frozen in `@heidi/patients/contracts` (ARCFE-1021).
// Both are re-exported only: the own-profiles body moved to the package at
// ARCFE-1053 and the EHR-provider body followed at ARCFE-1054, so each reads
// its identity in-package. The one identity still DECLARED here is
// `GET_TEAM_MEMBER_PATIENT_PROFILES` below, which is workspace's.
import {
  GET_PATIENT_PROFILES,
  GET_PATIENT_PROFILES_BY_EHR,
} from '@heidi/patients/contracts';

import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { getMemberPatientProfile } from '@/pages/api/scribeAPIV2';
import type {
  PaginatedPatientProfilesResult,
  PatientProfileWithSessionCount,
} from '@heidi/patients/contracts';
import {
  useQueryOwnPatientProfiles as useQueryOwnPatientProfilesCore,
  useQueryPatientProfilesByEHRProvider as useQueryPatientProfilesByEHRProviderCore,
} from '@heidi/patients/data';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { isViewingOtherSessionAtom } from '@heidi/session/data';
import {
  selectedReviewMemberAtom,
  useCombinedQueriesTeamGeneralInfo,
} from '@heidi/workspace/data';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useRBACPermissionCheck } from '../useRBACPermissionCheck';
import { useQueryUser } from './useQueryUser';

// paths
//
// STAYS APP-SIDE, and the ownership ruling turns on it (Di, 2026-08-12): this
// is the cache identity of `/teams/{teamId}/members/{memberId}/patient-profiles`
// — a TEAM MEMBER resource, so workspace's. It is a SEPARATE constant from
// `GET_PATIENT_PROFILES`, and every patients write that has to refresh this
// list does so with its own explicit invalidation under this key, never by a
// prefix that catches both. Two identities, two owners.
export const GET_TEAM_MEMBER_PATIENT_PROFILES = `get_team_member_patient_profiles`;

type UseQueryPatientProfilesByEHRProps = {
  ehrProvider: EHRProvider;
  ehrPatientId?: string;
};

interface UseQueryPatientProfilesOptions {
  /**
   * Gate for the fetch itself (AND-ed with the auth/scope conditions).
   * Defaults to true so the ~30 existing call sites are unaffected.
   * Callers that mount eagerly but only sometimes need the roster
   * (e.g. the local-search layer in the always-mounted session header)
   * pass false to avoid firing the unpaginated GET /patient-profiles/
   * for nothing. Reads still share the cache when another consumer has
   * fetched it.
   */
  enabled?: boolean;
  /** 1-based page number for server-side pagination. Omit for the full-list (flag-off) arm. */
  page?: number;
  /** Page size for server-side pagination. Defaults to 25 when `page` is provided. */
  pageSize?: number;
  /** Debounced search term forwarded as `q` to POST /v2.1/patient-profiles/. */
  search?: string;
}

/**
 * The review-mode BRANCH. It stays here by ruling (Di, 2026-08-12): the two
 * arms read different RESOURCES with different owners, and injecting the
 * team-member arm into `@heidi/patients` as a port would hide a workspace
 * route inside patients behind a callback — the shape design D7 forbids,
 * and one no import graph can see.
 */
export function useQueryPatientProfiles(
  options: UseQueryPatientProfilesOptions & { page: number }
): UseQueryResult<
  PaginatedPatientProfilesResult | PatientProfileWithSessionCount[],
  Error
>;
export function useQueryPatientProfiles(
  options?: UseQueryPatientProfilesOptions
): UseQueryResult<PatientProfileWithSessionCount[], Error>;
export function useQueryPatientProfiles(
  options?: UseQueryPatientProfilesOptions
): UseQueryResult<
  PatientProfileWithSessionCount[] | PaginatedPatientProfilesResult | undefined,
  Error
> {
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);
  const ownPatientProfiles = useQueryOwnPatientProfiles(options);
  // Team-member arm: `page`/`pageSize`/`search` are forwarded through the
  // options interface for interface parity, but the underlying
  // `getMemberPatientProfile` endpoint does not support server pagination yet —
  // the team-member arm stays client-side until that BE work lands.
  const teamMemberPatientProfiles = useQueryTeamMemberPatientProfiles(options);

  // Cast required: TypeScript resolves overloads statically and can't prove that
  // `options.page` flows through to the paginated arm at the type level. Callers
  // see the overload signatures above which narrow correctly per call site.
  type WideResult = UseQueryResult<
    PatientProfileWithSessionCount[] | PaginatedPatientProfilesResult | undefined,
    Error
  >;
  if (isViewingOtherSession) return ownPatientProfiles as unknown as WideResult;
  if (isReviewingOtherUser) return teamMemberPatientProfiles as unknown as WideResult;
  return ownPatientProfiles as unknown as WideResult;
}

/**
 * App-side delegate for `@heidi/patients/data`.
 *
 * Five app reads stay here and cross the package seam as plain values at the
 * same render-time position: isAuthenticated and the user record collapse
 * into one `isAuthenticated` decision, and RBAC / the review-mode atom / the
 * router's session id cross as themselves.
 */
export function useQueryOwnPatientProfiles(
  options: UseQueryPatientProfilesOptions & { page: number }
): UseQueryResult<PaginatedPatientProfilesResult, Error>;
export function useQueryOwnPatientProfiles(
  options?: UseQueryPatientProfilesOptions
): UseQueryResult<PatientProfileWithSessionCount[], Error>;
export function useQueryOwnPatientProfiles({
  enabled = true,
  page,
  pageSize,
  search,
}: UseQueryPatientProfilesOptions = {}): UseQueryResult<
  PatientProfileWithSessionCount[] | PaginatedPatientProfilesResult,
  Error
> {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: userData } = useQueryUser((data) => data?.kinde_user_id);
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);
  const currentSessionId = useCurrentSessionId();
  // Cast required: TypeScript resolves the core hook's overloads against
  // `page?: number` (optional) and picks the non-paginated arm, so the inferred
  // type loses PaginatedPatientProfilesResult. The overloads above correctly
  // narrow the return for callers based on whether they supply `page`.
  return useQueryOwnPatientProfilesCore({
    enabled,
    isAuthenticated: isAuthenticated && !!userData,
    isReviewingOtherUser,
    isViewingOtherSession,
    currentSessionId,
    page,
    pageSize,
    search,
  }) as unknown as UseQueryResult<
    PatientProfileWithSessionCount[] | PaginatedPatientProfilesResult,
    Error
  >;
}

export function useQueryTeamMemberPatientProfiles({
  enabled = true,
}: UseQueryPatientProfilesOptions = {}) {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: userData } = useQueryUser();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const teamId = teamDetails?.team.id;
  const selectedReviewMember = useAtomValue(selectedReviewMemberAtom);
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);
  return useQuery({
    queryKey: [
      GET_TEAM_MEMBER_PATIENT_PROFILES,
      teamId,
      selectedReviewMember,
      isReviewingOtherUser,
    ],
    enabled:
      enabled &&
      isAuthenticated &&
      !!userData &&
      !!teamId &&
      !!selectedReviewMember &&
      isReviewingOtherUser &&
      !isViewingOtherSession,
    queryFn: async () => {
      if (teamId && selectedReviewMember && isReviewingOtherUser) {
        const { data } = await getMemberPatientProfile(teamId, selectedReviewMember);
        return data.data.sort((a, b) => {
          return a.demographic_string?.localeCompare(b.demographic_string || '') || 0;
        });
      }
    },
  });
}

/**
 * App-side delegate for `@heidi/patients/data` (ARCFE-1054).
 *
 * The auth pair is the only app read this hook ever had, and it collapses into
 * one `isAuthenticated` decision at the same render-time position — the
 * ratified cohort-C shape its sibling above already uses. The call signature is
 * unchanged, so `useEpicLaunchComplete` (its one consumer) does not move.
 */
export function useQueryPatientProfilesByEHRProvider({
  ehrProvider,
  ehrPatientId,
}: UseQueryPatientProfilesByEHRProps) {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: userData } = useQueryUser((data) => data.kinde_user_id);

  return useQueryPatientProfilesByEHRProviderCore({
    ehrProvider,
    ehrPatientId,
    isAuthenticated: isAuthenticated && !!userData,
  });
}

export { GET_PATIENT_PROFILES, GET_PATIENT_PROFILES_BY_EHR };
